// pages/admin/payroll/PayrollHistory.jsx

import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { getAllPayrollRuns, deletePayrollRun } from "@/services/payroll.service";
import { formatCurrency } from "@/lib/utils";
import { Eye, FileText, History, Filter, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import ConfirmDialog from "@/components/ConfirmDialog";
import { createCanHelpers } from "@/utils/rbac";
import { usePermission } from "@/contexts/RoleAccessContext";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { getErrorMessage, isAuthExpiryError } from "@/utils/errorHandler";

const PAGE_SIZE = 6;

// Reimbursements are paid on top of salary and after statutory deductions, so they sit
// outside gross and outside total_deductions. Without showing them the modal reads as
// broken arithmetic: gross - deductions never reaches the net pay actually paid out.
const sumReimbursements = (emp) =>
  (emp?.reimbursements || []).reduce(
    (sum, claim) => sum + (Number(claim.amount) || 0),
    0
  );

// Mirrors the server's ordering. Numeric on purpose: regular runs store period_month
// unpadded ("8") while one-time runs pad it ("08"), so comparing as strings misorders.
const runOrdinal = (run) => [
  Number(run?.period_year) || 0,
  Number(run?.period_month) || 0,
  new Date(run?.run_date || run?.createdAt || 0).getTime() || 0,
];

const compareRuns = (a, b) => {
  const left = runOrdinal(a);
  const right = runOrdinal(b);

  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) return left[i] < right[i] ? -1 : 1;
  }

  return 0;
};

const PayrollHistory = () => {
  const [payrollHistory, setPayrollHistory] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [typeFilter, setTypeFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const { can } = usePermission();
  const { canEdit } = createCanHelpers(can);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const res = await getAllPayrollRuns();
      setPayrollHistory(res.data || []);
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to load payroll history"));
      }
      setPayrollHistory([]);
    }
  };

  /* --------------------------------------------
     DELETE A COMPLETED RUN

     Final settlement runs are excluded — reversing an exit is handled
     separately, so those cards never offer the action.
  ---------------------------------------------*/
  const handleDeleteRun = async () => {
    if (!canEdit("run_payroll", true)) return;
    if (!deleteTarget?._id || deleting) return;

    setDeleting(true);

    try {
      await deletePayrollRun(deleteTarget._id);
      toast.success("Payroll run deleted successfully");

      // The open details modal may be showing the run we just removed.
      if (String(selectedRun?._id) === String(deleteTarget._id)) {
        setShowDetailsModal(false);
        setSelectedRun(null);
      }

      setDeleteTarget(null);
      setCurrentPage(1);
      await loadHistory();
    } catch (error) {
      if (!isAuthExpiryError(error)) {
        toast.error(getErrorMessage(error, "Failed to delete payroll run"));
      }
    } finally {
      setDeleting(false);
    }
  };

  const describeDeletion = (run) => {
    if (!run) return "";

    const monthName = new Date(
      run.period_year,
      run.period_month - 1
    ).toLocaleString("default", { month: "long" });

    const label =
      run.type === "one_time"
        ? run.title || "One-time payroll"
        : `${monthName} ${run.period_year} payroll`;

    return `${label} will be permanently removed. Loan EMIs collected in this run go back onto the loans, reimbursement claims return to approved so they can be paid again, any leave encashment goes back to pending approval, and payslip notifications are cleared.`;
  };

  // A later run (same type) that this deletion would sit underneath. The server
  // blocks deleting a COMPLETED run when any later run exists — even one still in
  // progress (e.g. next month's draft) — so mirror that here to warn + disable
  // before the click instead of only erroring after it.
  const laterRunForTarget = deleteTarget
    ? payrollHistory
        .filter(
          (r) =>
            String(r._id) !== String(deleteTarget._id) &&
            r.type === deleteTarget.type &&
            compareRuns(r, deleteTarget) > 0,
        )
        .sort(compareRuns)[0] || null
    : null;
  const deleteBlocked =
    deleteTarget?.status === "completed" && !!laterRunForTarget;
  const laterRunForTargetLabel = laterRunForTarget
    ? laterRunForTarget.type === "one_time"
      ? laterRunForTarget.title || "One-time payroll"
      : `${new Date(
          laterRunForTarget.period_year,
          laterRunForTarget.period_month - 1,
        ).toLocaleString("default", { month: "long" })} ${laterRunForTarget.period_year}`
    : "";

  /* --------------------------------------------
     FILTER + SORT
  ---------------------------------------------*/
  const filteredHistory = useMemo(() => {
    return payrollHistory
      .filter((run) => (typeFilter === "all" ? true : run.type === typeFilter))
      .filter((run) => run.status === "completed")
      .sort(
        (a, b) =>
          new Date(b.period_year, b.period_month - 1) -
          new Date(a.period_year, a.period_month - 1)
      );
  }, [payrollHistory, typeFilter]);

  /* --------------------------------------------
     WHICH RUNS MAY BE DELETED

     Only the newest completed run of each type — the server enforces the same rule,
     so an older card offering a Delete button would just fail. Derived from the full
     history rather than the filtered view, so changing the type filter never changes
     which runs are deletable.
  ---------------------------------------------*/
  const deletableRunIds = useMemo(() => {
    const latestByType = new Map();

    payrollHistory
      .filter((run) => run.status === "completed" && run.type !== "termination")
      .forEach((run) => {
        const key = run.type || "regular";
        const current = latestByType.get(key);

        if (!current || compareRuns(run, current) > 0) {
          latestByType.set(key, run);
        }
      });

    return new Set(
      Array.from(latestByType.values()).map((run) => String(run._id))
    );
  }, [payrollHistory]);

  const totalPages = Math.ceil(filteredHistory.length / PAGE_SIZE);

  const paginatedRuns = filteredHistory.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const getTypeBadge = (type) => {
    switch (type) {
      case "termination":
        return (
          <span className="text-xs px-3 py-1 rounded-full font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            FINAL SETTLEMENT
          </span>
        );
      case "one_time":
        return (
          <span className="text-xs px-3 py-1 rounded-full font-semibold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
            ONE-TIME
          </span>
        );
      default:
        return (
          <span className="text-xs px-3 py-1 rounded-full font-semibold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            REGULAR
          </span>
        );
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <History className="w-6 h-6 text-blue-600" />
          Payroll History
        </CardTitle>

        {/* 🔍 TYPE FILTER */}
        <div className="w-56">
          <Select
            value={typeFilter}
            onValueChange={(v) => {
              setTypeFilter(v);
              setCurrentPage(1);
            }}
          >
            <SelectTrigger>
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Payrolls</SelectItem>
              <SelectItem value="regular">Regular</SelectItem>
              <SelectItem value="termination">Final Settlement</SelectItem>
              <SelectItem value="one_time">One-Time</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent>
        {filteredHistory.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
            No payroll records found for selected filter.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {paginatedRuns.map((run) => {
                const monthName = new Date(
                  run.period_year,
                  run.period_month - 1
                ).toLocaleString("default", { month: "long" });

                const totalNet = run.employees.reduce(
                  (sum, e) => sum + (e.net_pay || 0),
                  0
                );

                return (
                  <div
                    key={run._id}
                    onClick={() => {
                      setSelectedRun(run);
                      setShowDetailsModal(true);
                    }}
                    className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-md hover:shadow-lg hover:bg-muted/50 transition-all cursor-pointer"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {monthName} {run.period_year}
                        </p>

                        <p className="text-xl font-bold mt-1">
                          {run.employees.length} Employees
                        </p>
                      </div>

                      {getTypeBadge(run.type)}
                    </div>

                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Total Net Pay</span>
                        <span className="font-semibold text-emerald-600">
                          {formatCurrency(totalNet)}
                        </span>
                      </div>

                      <div className="flex justify-between">
                        <span>Run Date</span>
                        <span>{new Date(run.run_date).toLocaleString()}</span>
                      </div>
                    </div>

                    <div
                      className="mt-4 flex justify-end gap-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedRun(run);
                          setShowDetailsModal(true);
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Details
                      </Button>

                      {deletableRunIds.has(String(run._id)) && (
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setDeleteTarget(run)}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PAGINATION */}
            <div className="flex justify-center gap-6 mt-6">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                Previous
              </Button>

              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </p>

              <Button
                variant="outline"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </CardContent>

      {/* DETAILS MODAL */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-primary" />
                Payroll Details
              </span>

              {selectedRun && getTypeBadge(selectedRun.type)}
            </DialogTitle>
          </DialogHeader>

          {selectedRun && (
            <div className="space-y-6">
              {/* Month / Year */}
              <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {new Date(
                    selectedRun.period_year,
                    selectedRun.period_month - 1
                  ).toLocaleString("default", {
                    month: "long",
                    year: "numeric",
                  })}
                </p>

                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Processed on:{" "}
                  <strong>
                    {new Date(selectedRun.run_date).toLocaleString()}
                  </strong>
                </p>
              </div>

              {/* Summary Boxes */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    Employees
                  </p>
                  <p className="text-2xl font-bold">
                    {selectedRun.employees.length}
                  </p>
                </div>

                <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg border border-emerald-200 dark:border-emerald-800">
                  <p className="text-sm text-emerald-700 dark:text-emerald-400">
                    Total Gross
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      selectedRun.employees.reduce(
                        (sum, e) => sum + (e.gross_pay || 0),
                        0
                      )
                    )}
                  </p>
                </div>

                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <p className="text-sm text-red-700 dark:text-red-400">
                    Total Deductions
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      selectedRun.employees.reduce(
                        (sum, e) => sum + (e.total_deductions || 0),
                        0
                      )
                    )}
                  </p>
                </div>

                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <p className="text-sm text-amber-700 dark:text-amber-400">
                    Reimbursements
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      selectedRun.employees.reduce(
                        (sum, e) => sum + sumReimbursements(e),
                        0
                      )
                    )}
                  </p>
                </div>

                <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                  <p className="text-sm text-purple-700 dark:text-purple-400">
                    Net Payable
                  </p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(
                      selectedRun.employees.reduce(
                        (sum, e) => sum + (e.net_pay || 0),
                        0
                      )
                    )}
                  </p>
                </div>
              </div>

              {/* Employee Details List */}
              <div className="space-y-3">
                {selectedRun.employees.map((emp, idx) => (
                  <details
                    key={idx}
                    className="border border-gray-300 dark:border-gray-700 rounded-lg p-4"
                  >
                    <summary className="cursor-pointer text-gray-900 dark:text-gray-200 font-semibold">
                      {emp.details.name} — {formatCurrency(emp.net_pay)}
                    </summary>

                    <div className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-300">
                      <p>
                        <strong>Gross Salary:</strong>{" "}
                        {formatCurrency(emp.gross_pay)}
                      </p>

                      <p>
                        <strong>Loan Deductions:</strong>{" "}
                        {formatCurrency(emp.loan_deductions || "0")}
                      </p>

                      <p>
                        <strong>Total Deductions:</strong>{" "}
                        <span className="text-red-600 dark:text-red-400">
                          - {formatCurrency(emp.total_deductions)}
                        </span>
                      </p>

                      <p>
                        <strong>Reimbursements:</strong>{" "}
                        <span className="text-amber-600 dark:text-amber-400">
                          + {formatCurrency(sumReimbursements(emp))}
                        </span>
                      </p>

                      <p>
                        <strong>Net Pay:</strong> {formatCurrency(emp.net_pay)}
                      </p>

                      {emp.reimbursements?.length > 0 && (
                        <div className="mt-2">
                          <strong>Reimbursement Claims:</strong>
                          <ul className="list-disc ml-6">
                            {emp.reimbursements.map((claim, i) => (
                              <li key={claim.claim_id || i}>
                                {claim.claim_type || "Claim"}:{" "}
                                {formatCurrency(claim.amount)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {emp.loans && emp.loans.length > 0 && (
                        <div className="mt-2">
                          <strong>Loans:</strong>
                          <ul className="list-disc ml-6">
                            {emp.loans.map((l) => (
                              <li key={l.loan_id}>
                                {l.type} — EMI: {formatCurrency(l.monthly_emi)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {emp.deductions_list?.length > 0 && (
                        <div className="mt-2">
                          <strong>Manual Deductions:</strong>
                          <ul className="list-disc ml-6">
                            {emp.deductions_list.map((d, i) => (
                              <li key={i}>
                                {d.name}: {formatCurrency(d.amount)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      <div className="mt-2">
                        <strong>System Deductions:</strong>
                        <ul className="list-disc ml-6">
                          <li>
                            PF: {formatCurrency(emp.deductions.epf_employee)}
                          </li>
                          <li>
                            ESI: {formatCurrency(emp.deductions.esi_employee)}
                          </li>
                          <li>
                            PT:{" "}
                            {formatCurrency(emp.deductions.professional_tax)}
                          </li>
                          <li>TDS: {formatCurrency(emp.deductions.tds)}</li>
                        </ul>
                      </div>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        title="Delete this payroll run?"
        description="This cannot be undone."
        message={
          deleteBlocked ? (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-amber-800 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300">
              A newer payroll —{" "}
              <strong>{laterRunForTargetLabel}</strong> (
              {laterRunForTarget?.status !== "completed"
                ? "in progress"
                : "completed"}
              ) — was built on top of this one. Deleting this period would leave
              it inconsistent. Delete{" "}
              <strong>{laterRunForTargetLabel}</strong> first.
            </div>
          ) : (
            describeDeletion(deleteTarget)
          )
        }
        confirmText={deleting ? "Deleting..." : "Delete"}
        cancelText="Cancel"
        destructive
        confirmDisabled={deleteBlocked}
        onConfirm={handleDeleteRun}
      />
    </Card>
  );
};

export default PayrollHistory;
